<template>
  <v-container fluid pa-0>
    <v-card tile flat>
      <v-card-title>
        <v-text-field
          rounded
          filled
          label="Tågnummer"
          v-model="filter.train_number"
          @keydown.enter="searchConfirm"
          type="number"
          dense
          :hide-details="true"
          class="mr-4"
        ></v-text-field>
        <v-menu
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              rounded
              filled
              label="Avgångsdatum"
              v-model="filter.departure_date"
              dense
              :hide-details="true"
              v-on="on"
              v-bind="attrs"
              readonly
              prepend-inner-icon="mdi-calendar-range"
              class="mr-4"
            ></v-text-field>
          </template>
          <v-date-picker
            first-day-of-week="1"
            v-model="filter.departure_date"
          ></v-date-picker>
        </v-menu>
        <!-- <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Filtrera"
          rounded
          filled
          hide-details
        ></v-text-field> -->
        <v-btn
          @click="searchConfirm"
          large
          rounded
          color="primary"
          :loading="pending"
        >
          <v-icon small left>mdi-magnify</v-icon>
          Sök
        </v-btn>
        <v-spacer />
        <v-dialog v-model="showEditModal" width="1200" persistent>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              :disabled="!selected || !selected.length"
              large
              v-bind="attrs"
              v-on="on"
              rounded
              color="primary"
            >
              <v-icon small class="pr-4">mdi-pencil-outline</v-icon>
              Editera
            </v-btn>
          </template>
          <DuwaEditModal
            :train-number="filter.train_number"
            v-if="showEditModal"
            @close="showEditModal = false"
          />
        </v-dialog>
      </v-card-title>
      <v-data-table
        v-model="selected"
        :loading="$apollo.loading"
        :headers="headers"
        :items="data"
        :search="search"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        :show-select="true"
        :item-key="itemKey"
        disable-pagination
        hide-default-footer
        fixed-header
        :height="tableHeight"
      >
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import inputTableHeight from '@/mixins/inputTableHeight'
import SEARCH_DUWA_QUERY from '../graphql/searchDuwa'
import { format } from 'date-fns'
import DuwaEditModal from './DuwaEditModal'
import orderBy from 'lodash.orderby'
import { sync } from 'vuex-pathify'
import gql from 'graphql-tag'

const headers = [
  {
    text: 'Station',
    value: 'location_signature'
  },
  {
    text: 'Utgångsdatum',
    value: '_advertised_date_at_location'
  },
  {
    text: 'Ank/Avg',
    value: 'activity_type'
  },
  {
    text: 'Tid',
    value: '_advertised_time_at_location'
  },
  {
    text: 'Trafiktyp',
    value: '_type_of_traffic'
  },
  {
    text: 'Produktinfo',
    value: '_product_information'
  },
  {
    text: 'Service',
    value: '_service'
  },
  {
    text: 'Avvikelse',
    value: '_deviation'
  },
  {
    text: 'Tågsammansättning',
    value: '_train_composition'
  },
  {
    text: 'Via från',
    value: '_via_from_location'
  },
  {
    text: 'Via till',
    value: '_via_to_location'
  },
  {
    text: 'Bokning',
    value: '_booking'
  },
  {
    text: 'Övrigt',
    value: '_other_information'
  }
]

export default {
  name: 'DuwaTable',

  components: {
    DuwaEditModal
  },

  mixins: [inputTableHeight(144)],

  apollo: {
    stops: {
      query: gql`
        query stops {
          stops {
            stop_name
            short_name
          }
        }
      `,

      result({ data }) {
        if (data.stops) {
          this.$store.set('duwa/stops', data.stops)
        }
      }
    }
  },

  data() {
    return {
      pending: false,
      showEditModal: false,
      headers,
      searchDuwa: [],
      search: '',
      sortDesc: true,
      itemKey: 'activity_id',
      sortBy: 'advertised_date_at_location',
      filter: {
        train_number: '',
        departure_date: format(new Date(), 'yyyy-MM-dd')
      }
    }
  },

  methods: {
    async searchConfirm() {
      if (!this.filter.train_number || !this.filter.train_number.length) {
        return
      }

      this.pending = true

      const { data } = await this.$apollo.query({
        query: SEARCH_DUWA_QUERY,

        variables: {
          filter: {
            train_number: this.filter.train_number,
            departure_date: this.filter.departure_date
          }
        },

        fetchPolicy: 'no-cache'
      })

      if (data.searchDuwa && data.searchDuwa.announcements) {
        const places = data.searchDuwa.announcements.map(
          x => x.location_signature
        )
        this.$store.set('duwa/places', [...new Set(places)])
      }
      this.$store.set('duwa/selected', [])

      this.searchDuwa = data.searchDuwa

      this.pending = false
    }
  },

  computed: {
    selected: sync('duwa/selected'),

    data() {
      if (!this.searchDuwa || !this.searchDuwa.announcements) {
        return []
      }

      const data = this.searchDuwa.announcements.map(item => {
        return {
          ...item,
          _advertised_date_at_location: format(
            new Date(item.advertised_time_at_location),
            'yyyy-MM-d'
          ),
          _advertised_time_at_location: format(
            new Date(item.advertised_time_at_location),
            'HH:mm'
          ),
          _type_of_traffic: item.type_of_traffic
            ? item.type_of_traffic.map(x => x.description).join(', ')
            : '',
          _service: item.service
            ? item.service.map(x => x.description).join(', ')
            : '',
          _deviation: item.deviation
            ? item.deviation.map(x => x.description).join(', ')
            : '',
          _booking: item.booking
            ? item.booking.map(x => x.description).join(', ')
            : '',
          _via_from_location: item.via_from_location
            ? item.via_from_location.map(x => x.location_name).join(', ')
            : '',
          _via_to_location: item.via_to_location
            ? item.via_to_location.map(x => x.location_name).join(', ')
            : '',
          _other_information: item.other_information
            ? item.other_information.map(x => x.description).join(', ')
            : '',
          _product_information: item.product_information
            ? item.product_information.map(x => x.description).join(', ')
            : '',
          _train_composition: item.train_composition
            ? item.train_composition.map(x => x.description).join(', ')
            : ''
        }
      })
      this.$store.set('duwa/advertised_train_ident', this.filter.train_number)
      this.$store.set(
        'duwa/scheduled_departure_date_time',
        this.filter.departure_date
      )
      return orderBy(data, ['advertised_time_at_location'], ['asc'])
    }
  }
}
</script>
